import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PupCard from './PupCard';
import CountdownBar from '../inGameLayout/CountdownBar';
import { pupPlayCleared } from '../../redux/pupSlice';
import { pickPupValue, pickPupOptIn, votePupPlay, buryPupCard, performPupTrick } from '../../redux/pupApi';
import { selectPupCard } from '../../redux/pupCatalogSlice';
import './PupPlayOverlay.css';

const SUIT_GLYPHS = { h: '♥', d: '♦', c: '♣', s: '♠' };

// The rank buttons a valuing card (Go Fish) offers, in board order. "10" is what the
// server expects for a ten (it normalizes to the token rank 'T').
const VALUE_RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

// '62%' -> 62 (table-geometry positions are percent strings)
const pct = (s) => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

// The played card is pinned so its BOTTOM edge sits this many px above the top of the
// community cards (measured live — the community block recenters with the pot/board, so a
// fixed CSS percentage can't hold the gap). A few px keeps it clear of, but hugging, the board.
const GAP_ABOVE_BOARD = 6;

// Fallback line anchor (table-percent space: x from left, y from top) used for the one frame
// before the card is measured. Once measured, the line starts from the card's real bottom-center.
const CARD_ANCHOR = { x: 50, y: 44 };

// A Power-Up play in progress at this table: the played card sits face-up on the felt for
// everyone. An untargeted card shows for its reveal window (mode 'reveal', 2s) before the
// server applies its effect; a targeting card shows while its owner picks a target (mode
// 'targeting', 5s window), then a glowing line runs from the card to the chosen seat for
// the 2s line beat (mode 'line') before it resolves and the overlay clears. A discard card
// (mode 'discarding', 5s window) shows while its owner clicks one of their OWN hole cards,
// then flips to the ordinary 'reveal' beat (the server re-sends PUP_PLAY_PENDING). During
// the pre-resolve windows ('reveal'/'line') the card wears the blue shimmering border — the
// beat a future "interrupt" Power-Up will land in. Renders INSIDE its own .game-table
// (split-screen safe) with pointer-events: none throughout, so the seat clicks that pick
// the target — and the hole-card clicks that pick the discard — pass straight through to
// the components underneath.
// canOptIn: this viewer may still JOIN an opt-in play (mode 'optIn' — Flying Blind): seated,
// in the round, not the player, not already opted (GameTable decides). optedNames: display
// names of the seats that have joined so far (the play's optedSeats resolved by GameTable).
// canVote: this viewer may still ANSWER a voting play (mode 'voting' — Negotiation): unfolded,
// dealt in, not the player, not yet voted (GameTable decides). yesNames/noNames: the public
// tally resolved to display names. currentPickerName: whose window a seat-picking play (mode
// 'seatPicking' — Binding Contract) is on; picksSummary: "picker → picked" display lines.
// canTrick: this is the hero's own Magic Trick window (mode 'tricking') — mount the
// three-action assembly island; trickHoleCount sizes its hole-card index pickers.
// freezeActive: a player has TIME FROZEN over this window — the server paused it with its
// remaining time preserved, so hide the (now lying) countdown and hold the self-dismiss;
// the unfreeze re-broadcasts the pending event with an adjusted startTime, remounting the
// bar at the true remaining (playId dedupe absorbs the re-send).
const PupPlayOverlay = ({ roomId, play, seatPositions, mySeat, canOptIn = false, optedNames = [],
                         canVote = false, yesNames = [], noNames = [],
                         currentPickerName = null, picksSummary = [],
                         chosenSuit = null, onSuitPick = null,
                         canTrick = false, trickHoleCount = 0,
                         freezeActive = false,
                         active = true, stackIndex = 0 }) => {
  const dispatch = useDispatch();
  const isMine = mySeat != null && play.seat === mySeat;
  // A counter (Cancel) announces which pending card it is killing while both sit on the felt.
  const counteredEntry = useSelector((state) => selectPupCard(state, play.counteredCard));
  // Premonition's bury island shows the four privately peeked deck tokens (kind 'deck').
  const deckPeek = useSelector((state) => state.pup.deckPeeks[roomId]);
  // The card is in the window right before its effect fires — resolve is now inevitable
  // (barring an interrupt). The shimmer marks exactly this state, and only on the ACTIVE (top)
  // card: a paused base under an interrupt is frozen, not resolving.
  const resolving = active && (play.mode === 'reveal' || play.mode === 'line' || play.mode === 'cardLine');
  const windowDurationMs = Number(play.durationMs) || 5000;
  const countdownStartTime = play.startTime != null && Number.isFinite(Number(play.startTime))
    ? Number(play.startTime) : undefined;
  // One pick per valuing window (mirrors HoleCards' discardPickRef).
  const valuePickRef = useRef(false);
  useEffect(() => { valuePickRef.current = false; }, [play.mode, play.startTime]);
  // One join per opt-in window. NOT reset on play.startTime alone — every opt-in re-sends
  // the pending event with the SAME startTime, so this only re-arms on a genuinely new play.
  const optInRef = useRef(false);
  useEffect(() => { optInRef.current = false; }, [play.mode, play.startTime]);
  // One vote per voting window. Keyed by playId (NOT startTime): every vote re-sends the
  // pending event with the SAME startTime, so only a genuinely new play re-arms this.
  const voteRef = useRef(false);
  useEffect(() => { voteRef.current = false; }, [play.mode, play.playId]);
  // One bury pick per WINDOW STATE: a multi-bury window (Clairvoyance) re-broadcasts with a
  // decremented buriesLeft after each accepted bury, which re-arms the guard for the next.
  const buryRef = useRef(false);
  useEffect(() => { buryRef.current = false; }, [play.mode, play.playId, play.buriesLeft]);
  // Magic Trick's assembly: which of the four actions are armed. shift/suit hold their
  // sub-picks ({ sign|suit, idx }) and only COUNT once fully specified. One post per window.
  const trickRef = useRef(false);
  const [trick, setTrick] = useState({ peek2: false, extraCard: false, shift: null, suit: null });
  useEffect(() => {
    trickRef.current = false;
    setTrick({ peek2: false, extraCard: false, shift: null, suit: null });
  }, [play.mode, play.playId]);

  const overlayRef = useRef(null);
  const cardScaleRef = useRef(null);
  // Measured placement: { top } px for the card element (so its bottom is GAP_ABOVE_BOARD px
  // above the community cards) and { anchorY } table-percent for the card→target line origin.
  const [layout, setLayout] = useState(null);

  // Pin the on-felt card just above the community cards. The community block is centered on
  // the felt and grows/shrinks with the pot + board, so we read its live top rather than
  // guessing a percentage. Scoped to THIS overlay's own .game-table (offsetParent) so
  // split-screen never measures the other table. useLayoutEffect + a ResizeObserver keep it
  // in sync through the deal animation and window resizes, with no visible pre-paint jump.
  useLayoutEffect(() => {
    const overlay = overlayRef.current;
    const cardScale = cardScaleRef.current;
    const table = overlay?.offsetParent;
    const community = table?.querySelector('.community-cards');
    if (!table || !community || !cardScale) return;

    const measure = () => {
      const tableRect = table.getBoundingClientRect();
      const commRect = community.getBoundingClientRect();
      if (!tableRect.height) return;
      const cardBottom = (commRect.top - tableRect.top) - GAP_ABOVE_BOARD;
      setLayout({
        top: Math.max(0, cardBottom - cardScale.offsetHeight),
        anchorY: (cardBottom / tableRect.height) * 100,
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(community);
    ro.observe(table);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Self-heal: server WS pushes are one-shot — if the PUP_PLAY_RESOLVED/CANCELLED frame is
  // missed (reconnect window), drop THIS play (by playId) shortly after its window must have
  // ended server-side rather than leaving a card glued to the felt. State heals via resync.
  // Only for the ACTIVE card: a paused base under an interrupt has no live window, so its stale
  // receivedAt/durationMs must NOT trigger a premature clear — it re-arms when it re-surfaces.
  // Also held while time is frozen: the window's deadline stopped moving server-side, so the
  // stale receivedAt would dismiss a play that is legitimately still pending; the unfreeze
  // re-broadcast (fresh receivedAt + adjusted startTime) re-arms it.
  useEffect(() => {
    if (!active || freezeActive) return undefined;
    const explicitDeadline = play.deadline != null && Number.isFinite(Number(play.deadline))
      ? Number(play.deadline) : null;
    const serverDeadline = explicitDeadline ?? (countdownStartTime != null
      ? countdownStartTime + windowDurationMs : null);
    const fallbackDeadline = (play.receivedAt || Date.now()) + windowDurationMs;
    const remaining = Math.max(0, (serverDeadline ?? fallbackDeadline) - Date.now()) + 1500;
    const t = setTimeout(() => dispatch(pupPlayCleared({ roomId, playId: play.playId })), remaining);
    return () => clearTimeout(t);
  }, [dispatch, roomId, play, active, freezeActive, countdownStartTime, windowDurationMs]);

  // Seat-target line ('line') and card-target line ('cardLine', hole zone: points at the
  // owning seat — the hero's own for OWN_HOLE picks). Community picks have targetSeat -1.
  const targetPos = (play.mode === 'line' || play.mode === 'cardLine')
      && play.targetSeat != null && play.targetSeat >= 0
    ? seatPositions[play.targetSeat]
    : null;
  // Geometry positions are {bottom, left} percents; SVG space runs top-down.
  const lineTo = targetPos
    ? { x: pct(targetPos.left), y: 100 - pct(targetPos.bottom) }
    : null;

  // The seat that PLAYED this card — pinned throughout the freeze (every mode). A thin curved
  // line runs from the card to that seat, pairing with the seat's violet border highlight, so
  // it's always clear whose Power-Up is on the felt. Cleared when the overlay unmounts on
  // resolve. play.seat is present in every PUP_PLAY_PENDING/TARGET_CHOSEN payload.
  const playerPos = play.seat != null && play.seat >= 0 ? seatPositions[play.seat] : null;
  const playerLineTo = playerPos
    ? { x: pct(playerPos.left), y: 100 - pct(playerPos.bottom) }
    : null;

  const handleValuePick = async (value) => {
    if (valuePickRef.current) return;
    valuePickRef.current = true;
    try {
      await dispatch(pickPupValue({ roomId, value })).unwrap();
    } catch (e) {
      valuePickRef.current = false; // rejected — let the player pick again in the window
    }
  };

  const handleOptIn = async () => {
    if (optInRef.current) return;
    optInRef.current = true;
    try {
      await dispatch(pickPupOptIn({ roomId })).unwrap();
    } catch (e) {
      optInRef.current = false; // rejected — the window may still be open, allow a retry
    }
  };

  const handleVote = async (vote) => {
    if (voteRef.current) return;
    voteRef.current = true;
    try {
      await dispatch(votePupPlay({ roomId, vote })).unwrap();
    } catch (e) {
      voteRef.current = false; // rejected — the window may still be open, allow a retry
    }
  };

  const handleBury = async (index) => {
    if (buryRef.current) return;
    buryRef.current = true;
    try {
      await dispatch(buryPupCard({ roomId, index })).unwrap();
    } catch (e) {
      buryRef.current = false; // rejected — the window may still be open, allow a retry
    }
  };

  // How many of the trick's four actions are fully specified (shift/suit need their
  // sub-picks complete before they count).
  const trickCount = (trick.peek2 ? 1 : 0) + (trick.extraCard ? 1 : 0)
    + (trick.shift && trick.shift.sign && trick.shift.idx != null ? 1 : 0)
    + (trick.suit && trick.suit.suit && trick.suit.idx != null ? 1 : 0);

  const handleTrickConfirm = async () => {
    if (trickRef.current || trickCount !== 3) return;
    trickRef.current = true;
    const actions = [];
    if (trick.peek2) actions.push('peek2');
    if (trick.extraCard) actions.push('extraCard');
    if (trick.shift && trick.shift.sign && trick.shift.idx != null) {
      actions.push(`shift:${trick.shift.sign}:${trick.shift.idx}`);
    }
    if (trick.suit && trick.suit.suit && trick.suit.idx != null) {
      actions.push(`suit:${trick.suit.suit}:${trick.suit.idx}`);
    }
    try {
      await dispatch(performPupTrick({ roomId, actions })).unwrap();
    } catch (e) {
      trickRef.current = false; // rejected — the window may still be open, allow a retry
    }
  };

  const anchorY = layout ? layout.anchorY : CARD_ANCHOR.y;

  // Quadratic-Bezier arc from the card's bottom-center to the playing seat: bow the control
  // point off the midpoint (perpendicular to the chord) for a gentle curve rather than a
  // straight run. Table-percent space (the SVG stretches 0..100 over the container), so the
  // bow is a soft visual arc, not a metric one — exactly what's wanted for a decorative link.
  const playerCurve = playerLineTo ? (() => {
    const ax = CARD_ANCHOR.x, ay = anchorY;
    const bx = playerLineTo.x, by = playerLineTo.y;
    const dx = bx - ax, dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    const bow = Math.min(16, len * 0.2);
    const cx = (ax + bx) / 2 + (-dy / len) * bow;
    const cy = (ay + by) / 2 + (dx / len) * bow;
    return `M ${ax} ${ay} Q ${cx} ${cy} ${bx} ${by}`;
  })() : null;

  // Stacked (paused) cards peek behind the active one: shifted up + dimmed + lower z, so an
  // interrupt on top and the card it paused are both visible on the felt.
  const overlayStyle = stackIndex > 0
    ? { opacity: Math.max(0.4, 1 - stackIndex * 0.22), zIndex: 30 - stackIndex }
    : undefined;
  const cardTop = layout ? layout.top - stackIndex * 16 : null;

  return (
    <div className="pup-play-overlay" ref={overlayRef} style={overlayStyle}>
      {active && playerCurve && (
        <svg className="pup-player-line" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d={playerCurve} />
        </svg>
      )}
      {active && lineTo && (
        <svg className="pup-target-line" viewBox="0 0 100 100" preserveAspectRatio="none">
          <line x1={CARD_ANCHOR.x} y1={anchorY} x2={lineTo.x} y2={lineTo.y} />
        </svg>
      )}
      <div className="pup-table-card" style={cardTop != null ? { top: `${cardTop}px` } : undefined}>
        <div
          className={`pup-table-card-scale${resolving ? ' pup-table-card-resolving' : ''}`}
          ref={cardScaleRef}
        >
          {/* Blue shimmering border ring, hugging the card just outside its edge. */}
          {resolving && <div className="pup-shimmer-ring" />}
          {/* play.card is the card's wire CODE; PupCard resolves it via the cached catalog. */}
          <PupCard code={play.card} roomId={roomId} />
        </div>
        {/* One bar per window: 5s targeting, then a fresh 2s bar for the line beat.
            Keyed by mode+startTime so each server window mounts a full bar. Only the ACTIVE
            (top) card has a live window; a paused base shows no countdown — and neither
            does a window a player froze time over (paused server-side at its remaining;
            the unfreeze re-broadcast's adjusted startTime remounts the bar truthfully). */}
        {active && !freezeActive && (
          <CountdownBar
            key={`${play.mode}-${play.startTime || 0}`}
            className="pup-window-countdown"
            durationMs={windowDurationMs}
            startTime={countdownStartTime}
          />
        )}
        {active && play.mode === 'targeting' && (
          <div className="pup-table-card-label">
            {isMine ? 'Click a player to target' : 'Choosing a target…'}
          </div>
        )}
        {active && play.mode === 'discarding' && (
          <div className="pup-table-card-label">
            {isMine ? 'Click one of your cards to discard' : 'Choosing a card to discard…'}
          </div>
        )}
        {/* Swiper's cost window: the picker is the hero's own DOCK (PupHand pulses). */}
        {active && play.mode === 'pupDiscarding' && (
          <div className="pup-table-card-label">
            {isMine ? 'Click one of your Power-Up cards to discard'
              : 'Choosing a Power-Up card to discard…'}
          </div>
        )}
        {active && play.mode === 'cardTargeting' && (
          <div className="pup-table-card-label">
            {isMine ? 'Click one of your cards to change' : 'Choosing a card…'}
          </div>
        )}
        {active && play.mode === 'valuing' && (
          <div className="pup-table-card-label">
            {isMine ? 'Name a card value' : 'Naming a card value…'}
          </div>
        )}
        {active && play.mode === 'optIn' && (
          <div className="pup-table-card-label">
            {isMine ? 'You are flying blind — others may join…' : 'Flying blind — join in?'}
          </div>
        )}
        {/* A counter (Cancel) shimmering through its reveal window — say what it's killing. */}
        {active && play.mode === 'reveal' && play.counteredCard && (
          <div className="pup-table-card-label">
            Countering {counteredEntry?.name || play.counteredCard}…
          </div>
        )}
        {active && play.mode === 'voting' && (
          <div className="pup-table-card-label">
            {isMine ? 'You proposed splitting the pot — waiting on votes…'
              : 'Vote: split the pot evenly and end the round?'}
          </div>
        )}
        {/* The running public tally (the player who PLAYED it auto-voted yes). */}
        {active && play.mode === 'voting' && (yesNames.length > 0 || noNames.length > 0) && (
          <div className="pup-optin-joined">
            Yes: {yesNames.length ? yesNames.join(', ') : '—'}
            {' · '}
            No: {noNames.length ? noNames.join(', ') : '—'}
          </div>
        )}
        {/* Yes/No island for eligible voters — a pointer-events island like the rank picker. */}
        {active && play.mode === 'voting' && canVote && (
          <div className="pup-vote-actions">
            <button type="button" className="pup-optin-btn" onClick={() => handleVote(true)}>
              Split it
            </button>
            <button type="button" className="pup-optin-btn pup-vote-no-btn" onClick={() => handleVote(false)}>
              Play on
            </button>
          </div>
        )}
        {active && play.mode === 'voting' && !canVote && !isMine && mySeat != null
          && ((play.yesSeats || []).includes(mySeat) || (play.noSeats || []).includes(mySeat)) && (
          <div className="pup-optin-joined">You voted ✓</div>
        )}
        {active && play.mode === 'seatPicking' && (
          <div className="pup-table-card-label">
            {play.currentPickerSeat === mySeat
              ? 'Your pick — click an unfolded player'
              : `${currentPickerName || 'A player'} is picking…`}
          </div>
        )}
        {/* The picks made so far, publicly ("A → B" lines resolved by GameTable). */}
        {active && play.mode === 'seatPicking' && picksSummary.length > 0 && (
          <div className="pup-optin-joined">{picksSummary.join(' · ')}</div>
        )}
        {active && play.mode === 'multiDiscarding' && (
          <div className="pup-table-card-label">
            {isMine ? 'Click the cards to swap, then confirm on your hand'
              : 'Choosing cards to exchange…'}
          </div>
        )}
        {/* Super Suit's chosen-suit island: pick a suit FIRST, then click the hole card.
            GameTable passes onSuitPick only while the hero's own Super Suit is targeting. */}
        {active && play.mode === 'cardTargeting' && isMine && onSuitPick && (
          <div className="pup-suit-picker">
            {['h', 'd', 'c', 's'].map((s) => (
              <button
                key={s}
                type="button"
                className={`pup-value-picker-btn pup-suit-btn-${s}${chosenSuit === s ? ' pup-suit-btn-chosen' : ''}`}
                onClick={() => onSuitPick(s)}
              >
                {SUIT_GLYPHS[s]}
              </button>
            ))}
            <div className="pup-optin-joined">
              {chosenSuit ? 'Now click one of your cards' : 'Pick a suit first'}
            </div>
          </div>
        )}
        {active && play.mode === 'deckBury' && (
          <div className="pup-table-card-label">
            {play.card === 'ALL_SEEING_EYE'
              ? (isMine ? 'Discard one card from the deck?' : 'The eye is deciding…')
              : (isMine
                ? `Bury ${play.buriesLeft > 1 ? `up to ${play.buriesLeft} cards` : 'one card'} to the bottom of the deck?`
                : 'Deciding a premonition…')}
          </div>
        )}
        {/* The deck window island: the peeked tokens (owner only — the room never sees
            them) as buttons, plus a keep-rest escape. Premonition buries one to the bottom;
            All Seeing Eye BURNS one out of the deck; Clairvoyance's window stays open across
            up to two buries (the server re-pushes the fresh top four after each). */}
        {active && play.mode === 'deckBury' && isMine && (
          <div className="pup-bury-picker">
            {(deckPeek?.cards || []).map((token, i) => (
              <button
                key={`${token}-${i}`}
                type="button"
                className="pup-value-picker-btn"
                onClick={() => handleBury(i)}
                title={play.card === 'ALL_SEEING_EYE'
                  ? `Remove ${token} from the deck`
                  : `Send ${token} to the bottom`}
              >
                {token}
              </button>
            ))}
            <button
              type="button"
              className="pup-optin-btn"
              onClick={() => handleBury(-1)}
            >
              Keep {play.buriesLeft > 1 ? 'the rest' : 'all'}
            </button>
          </div>
        )}
        {active && play.mode === 'tricking' && (
          <div className="pup-table-card-label">
            {isMine ? `Pick three tricks (${trickCount}/3)` : 'Performing a magic trick…'}
          </div>
        )}
        {/* Magic Trick's assembly island (hero only): toggle exactly three of the four
            actions — the +-1 and re-suit rows carry inline sub-picks (sign/suit + which of
            the hero's own hole cards, 1-based buttons) — then confirm. A pointer-events
            island like the rank picker. */}
        {active && play.mode === 'tricking' && isMine && canTrick && (
          <div className="pup-trick-picker">
            <div className="pup-trick-row">
              <button
                type="button"
                className={`pup-optin-btn${trick.peek2 ? ' pup-suit-btn-chosen' : ''}`}
                onClick={() => setTrick((t) => ({ ...t, peek2: !t.peek2 }))}
              >
                Peek top 2
              </button>
              <button
                type="button"
                className={`pup-optin-btn${trick.extraCard ? ' pup-suit-btn-chosen' : ''}`}
                onClick={() => setTrick((t) => ({ ...t, extraCard: !t.extraCard }))}
              >
                Extra table card
              </button>
            </div>
            <div className="pup-trick-row">
              {['+1', '-1'].map((sign) => (
                <button
                  key={sign}
                  type="button"
                  className={`pup-value-picker-btn${trick.shift?.sign === sign ? ' pup-suit-btn-chosen' : ''}`}
                  onClick={() => setTrick((t) => ({
                    ...t,
                    shift: t.shift?.sign === sign ? null : { ...(t.shift || {}), sign },
                  }))}
                >
                  {sign}
                </button>
              ))}
              {Array.from({ length: trickHoleCount }, (_, i) => (
                <button
                  key={`shift-idx-${i}`}
                  type="button"
                  className={`pup-value-picker-btn${trick.shift?.idx === i ? ' pup-suit-btn-chosen' : ''}`}
                  onClick={() => setTrick((t) => ({
                    ...t,
                    shift: { ...(t.shift || {}), idx: t.shift?.idx === i ? null : i },
                  }))}
                  title={`Your card #${i + 1}`}
                >
                  #{i + 1}
                </button>
              ))}
              <span className="pup-trick-hint">±1 a card</span>
            </div>
            <div className="pup-trick-row">
              {['h', 'd', 'c', 's'].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`pup-value-picker-btn pup-suit-btn-${s}${trick.suit?.suit === s ? ' pup-suit-btn-chosen' : ''}`}
                  onClick={() => setTrick((t) => ({
                    ...t,
                    suit: t.suit?.suit === s ? null : { ...(t.suit || {}), suit: s },
                  }))}
                >
                  {SUIT_GLYPHS[s]}
                </button>
              ))}
              {Array.from({ length: trickHoleCount }, (_, i) => (
                <button
                  key={`suit-idx-${i}`}
                  type="button"
                  className={`pup-value-picker-btn${trick.suit?.idx === i ? ' pup-suit-btn-chosen' : ''}`}
                  onClick={() => setTrick((t) => ({
                    ...t,
                    suit: { ...(t.suit || {}), idx: t.suit?.idx === i ? null : i },
                  }))}
                  title={`Your card #${i + 1}`}
                >
                  #{i + 1}
                </button>
              ))}
              <span className="pup-trick-hint">Re-suit a card</span>
            </div>
            <button
              type="button"
              className="pup-optin-btn"
              disabled={trickCount !== 3}
              onClick={handleTrickConfirm}
            >
              Perform ({trickCount}/3)
            </button>
          </div>
        )}
        {/* Who has joined the opt-in so far (the player who PLAYED it is always in). */}
        {active && play.mode === 'optIn' && optedNames.length > 0 && (
          <div className="pup-optin-joined">Also blind: {optedNames.join(', ')}</div>
        )}
        {/* Join button for eligible viewers of an opt-in window. Like the rank picker, this
            is a pointer-events island on an otherwise click-through overlay. */}
        {active && play.mode === 'optIn' && canOptIn && (
          <div className="pup-optin-actions">
            <button type="button" className="pup-optin-btn" onClick={handleOptIn}>
              Fly blind too
            </button>
          </div>
        )}
        {active && play.mode === 'optIn' && !canOptIn && !isMine && mySeat != null
          && (play.optedSeats || []).includes(mySeat) && (
          <div className="pup-optin-joined">You are flying blind ✓</div>
        )}
        {/* Rank picker for the hero's own valuing window. The overlay is pointer-events:
            none throughout; ONLY this widget re-enables events so seat/hole-card clicks
            elsewhere still pass through. */}
        {active && play.mode === 'valuing' && isMine && (
          <div className="pup-value-picker">
            {VALUE_RANKS.map((rank) => (
              <button
                key={rank}
                type="button"
                className="pup-value-picker-btn"
                onClick={() => handleValuePick(rank)}
              >
                {rank}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PupPlayOverlay;
